export interface StockAssetPreview {
  asset_id: string
  logo: string | null
  name: string | null
  slug: string
  symbol: string
}

export interface StockAssetDetail {
  asset_id: string
  country: string | null
  currency: string | null
  dividend_price: string | null
  dividend_yield: string | null
  eps: string | null
  issuer_description: string | null
  issuer_name: string | null
  logo: string | null
  market_cap: string | null
  name: string | null
  pe_ratio: string | null
  previous_closing_price: string | null
  sector: string | null
  slug: string
  status: string
  symbol: string
  type: string
  week52_high_price: string | null
  week52_low_price: string | null
}

export type StockMarket = {
  slug: string
  prices: any[]
  token_id: number
  icon: string

  circulating_supply: number | null
  total_supply: number | null
  max_supply: number | null

  btc_price: number | null
  btc_marketcap: number | null
  btc_volume_24h: number | null
  btc_volume_30d: number | null
  btc_price_change_24h: number | null

  usd_price: number
  usd_marketcap: number
  usd_volume_24h: number
  usd_volume: number
  usd_volume_30d: number
  usd_price_change_24h: number
  usd_price_change_24h_abs: number
  usd_pe: number
  usd_earning_per_share: number
  usd_52_week_high: number
  usd_52_week_low: number
  usd_dividend_yield: number
  usd_dividend_per_share: number

  token_dominance_rate: number | null
  rank: number

  app_tradable: boolean
  exchange_tradable: boolean
  defi_tradable: boolean

  update_time: number
  price_update_time: number

  app_symbol: string

  all_time: {
    high: number
    high_timestamp: number
    low: number
    low_timestamp: number
  }

  visibility: 'public' | 'private'
  restricted_areas: any
  analyst_ratings: {
    buy: number
    hold: number
    sell: number
    target_price: number
    est_return: number
  }

  session_type: StockSessionType
  live_price?: number
  live_price_pct?: number
}

export type StockMeta = {
  name: string
  slug: string
  symbol: string
  active: boolean
  tags: string[]
  description: string
  platform: any[]
  icon: string
  id: number
  source_slug: string

  website_urls: string[]
  whitepaper_urls: string[]
  whitepaper_urls_v2: Array<{ url: string; is_mica?: boolean }>
  explorer_urls: string[]
  sourcecode_urls: string[]

  message_board_urls: string[]
  announcement_urls: string[]
  chat_urls: string[]
  telegram_urls: string[]
  discord_urls: string[]
  reddit_urls: string[]
  twitter_urls: string[]

  sustainability_disclosure_urls: string[]
  rss_feeds: Array<{ source: string | null; url: string; filters?: string }>

  is_defi_swap: boolean
  meta_title: string
  trading_view_symbol: string
  trading_view_status: number
  price_engine_enabled: boolean
  visibility: 'public' | 'private'

  // stock extras
  exchange?: string
  country?: string
  sector?: string
  industry?: string
}

export interface StockOverall extends StockMeta, StockMarket {}

export type LiveStockMarket = Partial<
  Pick<
    StockOverall,
    'usd_price' | 'usd_marketcap' | 'usd_price_change_24h' | 'usd_volume_24h' | 'symbol' | 'slug'
  >
>

export interface StockPrices {
  price_change: number
  prices: [number, number, number][] // [timestamp, price, market cap]
  usd_price_change: number
  overnight_usd_price: number
  overnight_usd_price_change: number
}

export enum StockSessionType {
  NoTrading = 'NO_TRADING',
  EarlyTrading = 'EARLY_TRADING',
  CoreTrading = 'CORE_TRADING',
  LateTrading = 'LATE_TRADING',
  Unknown = 'UNKNOWN',
}

export interface StockPriceInfo {
  price: string
  price_pct: string
  timestamp: number
}

export interface StockClosePriceInfo {
  close_date: number
  price: string
  price_pct: string
}

export interface StockLastPriceInfo {
  close_price: StockClosePriceInfo | null
  price_info: StockPriceInfo | null
  session_type: StockSessionType
}

export type StockRatingType = 'BUY' | 'HOLD' | 'SELL'

export interface StockRatings {
  asset_id: string
  buy: number
  hold: number
  sell: number
  target_price: number
  est_return: number
  rating: StockRatingType
}

export interface StocksContent {
  id: string
  description: {
    root: {
      type: string
      children: {
        type: any
        version: number
        [k: string]: unknown
      }[]
      direction: ('ltr' | 'rtl') | null
      format: 'left' | 'start' | 'center' | 'right' | 'end' | 'justify' | ''
      indent: number
      version: number
    }
    [k: string]: unknown
  }
  faq?:
    | {
        faq: {
          question: string
          answer: {
            root: {
              type: string
              children: {
                type: any
                version: number
                [k: string]: unknown
              }[]
              direction: ('ltr' | 'rtl') | null
              format: 'left' | 'start' | 'center' | 'right' | 'end' | 'justify' | ''
              indent: number
              version: number
            }
            [k: string]: unknown
          }
        }
        id?: string | null
      }[]
    | null
  selectedLangs?:
    | (
        | 'ar'
        | 'hr'
        | 'da'
        | 'nl'
        | 'en-us'
        | 'fr'
        | 'fr-CA'
        | 'de'
        | 'el'
        | 'id'
        | 'it'
        | 'ko'
        | 'no'
        | 'pl'
        | 'pt'
        | 'pt-br'
        | 'ro'
        | 'ru'
        | 'es'
        | 'sv'
        | 'zh-TW'
        | 'vi'
      )[]
    | null
  stock_id: string
  symbol: string
  crowdinFileEtag?:
    | {
        [k: string]: unknown
      }
    | unknown[]
    | string
    | number
    | boolean
    | null
  crowdinFileID?: number | null
  crowdinHTMLContentFileID?: number | null
  slug: string
  updatedAt: string
  createdAt: string
  _status?: ('draft' | 'published') | null
}
